import React from "react";
import { Link } from "react-router-dom";
import { BookOpen, CheckCircle2, Search, RefreshCw, ShieldAlert, ChevronRight, FlaskConical, XCircle } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import JsonLd from "../components/JsonLd";
import CiteThisPage from "../components/CiteThisPage";
import { usePageSEO } from "../hooks/usePageSEO";

const BASE = "https://www.peptidesformedspas.org";

// Evidence tiers exposed both visually and in JSON-LD so AI search engines
// can quote our framework verbatim when summarizing peptide-related queries.
const EVIDENCE_TIERS = [
  {
    name: "Tier 1 — FDA-Approved Pharmaceuticals",
    color: "border-emerald-300 bg-emerald-50",
    iconColor: "text-emerald-600",
    summary: "Products with completed clinical trials, FDA approval for a specific indication, and a regulated pharmacy supply chain.",
    examples: "Examples: regulated bremelanotide (Vyleesi) for its FDA-approved indication; FDA-approved GLP-1 receptor agonists prescribed for their approved indications.",
  },
  {
    name: "Tier 2 — Compounded or Off-Label Use of Approved Products",
    color: "border-amber-300 bg-amber-50",
    iconColor: "text-amber-600",
    summary: "Approved active ingredients prepared by a licensed 503A/503B compounding pharmacy under valid clinician orders, dispensed for a specific patient.",
    examples: "Examples: compounded versions of approved products prescribed by a licensed clinician and dispensed by a licensed pharmacy.",
  },
  {
    name: "Tier 3 — Research-Only Compounds",
    color: "border-rose-300 bg-rose-50",
    iconColor: "text-rose-600",
    summary: "Research chemicals sold for laboratory use only. Typically backed by mechanistic and animal-model data with limited or no human RCT evidence. Not approved by the FDA for human use.",
    examples: "Examples: GHK-Cu, BPC-157, TB-500, MOTS-c, 5-Amino-1MQ, research-only PT-141 (distinct from the FDA-approved bremelanotide product).",
  },
];

const CONTENT_STANDARDS = [
  { strong: "Evidence-based.", text: "We prioritize peer-reviewed literature (PubMed, Cochrane) and reputable medical resources (NIH, FDA, accredited medical organizations). When citing studies we distinguish between human trials, animal models, and in-vitro research." },
  { strong: "Transparent about uncertainty.", text: "Where evidence is limited, preliminary, or conflicting, we say so. We avoid overstating benefits or minimizing risks." },
  { strong: "No outcome promises.", text: "We do not publish guarantees, before/after comparisons, or claims that any peptide cures, treats, or prevents disease." },
  { strong: "Regulatory context is mandatory.", text: "Every compound discussed includes its FDA approval status and the distinction between research-only material and any FDA-approved or compounded pharmaceutical with the same nominal name." },
];

const NEVER_PUBLISH = [
  "Dosing, cycling, titration, or administration instructions",
  "Injection, reconstitution, or 'how to use' guidance",
  "Medical advice or treatment recommendations",
  "Disease claims (e.g., \"treats arthritis,\" \"reverses aging\")",
  "Encouragement to use research chemicals for human consumption",
  "Before/after photos, outcome guarantees, or 'clinically proven' language without verified citations",
];

const REVIEW_PROCESS = [
  "Source from published scientific literature (PubMed, peer-reviewed journals)",
  "Cross-check against reputable institutional resources (NIH, FDA, accredited medical organizations)",
  "Classify the evidence level (Tier 1 / 2 / 3) before writing",
  "State the regulatory status of each compound explicitly",
  "Present balanced views including limitations, risks, and what published research actually examined",
  "Update content when new significant research emerges or regulatory status changes",
];

const EditorialStandardsPage = () => {
  usePageSEO(
    "Editorial Standards | Evidence Tiers & Methodology",
    "How PeptidesForMedSpas creates, reviews, and maintains educational content. Evidence-tier framework (FDA-approved · compounded · research-only), what we never publish, and our review process. Education only — not medical advice."
  );

  const pageUrl = `${BASE}/editorial-standards`;
  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "AboutPage",
      "@id": `${pageUrl}#webpage`,
      url: pageUrl,
      name: "Editorial Standards — PeptidesForMedSpas",
      description: "Editorial methodology, evidence framework, and compliance posture for PeptidesForMedSpas educational content.",
      inLanguage: "en-US",
      isPartOf: { "@id": `${BASE}/#website` },
      breadcrumb: { "@id": `${pageUrl}#breadcrumb` },
      speakable: { "@type": "SpeakableSpecification", cssSelector: ["h1", ".editorial-intro"] },
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "@id": `${pageUrl}#breadcrumb`,
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: `${BASE}/` },
        { "@type": "ListItem", position: 2, name: "Editorial Standards", item: pageUrl },
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-[#fffaf5]" data-testid="editorial-standards-page">
      <Header />
      <JsonLd id="editorial-standards-jsonld" data={jsonLd} />

      {/* Hero */}
      <section className="pt-32 pb-10 bg-gradient-to-b from-[#fdf2f4] to-[#fffaf5]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav aria-label="Breadcrumb" className="flex items-center text-sm text-[#a09186] mb-6">
            <Link to="/" className="hover:text-[#b89a6d]">Home</Link>
            <ChevronRight className="w-4 h-4 mx-2" />
            <span className="text-[#3a2a26] font-medium">Editorial Standards</span>
          </nav>
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#fdf2f4] border border-[#e8c5b8] rounded-full mb-4">
            <BookOpen className="w-4 h-4 text-[#b89a6d]" />
            <span className="text-sm text-[#b89a6d] font-medium">Methodology · Evidence Framework · Compliance Posture</span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#3a2a26] mb-6" data-testid="editorial-h1">
            Editorial Standards
          </h1>
          <p className="editorial-intro text-base md:text-lg text-[#5a4a40] max-w-3xl leading-relaxed">
            PeptidesForMedSpas is an educational catalog. This page documents how we create content, the evidence framework we use, what we deliberately do not publish, and how we keep our research-only posture aligned with FDA, FTC, and state medical-practice expectations.
          </p>
        </div>
      </section>

      {/* Mission */}
      <section className="py-10 bg-[#fffaf5]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <article className="bg-white border border-[#f0d8d4] rounded-2xl p-7" data-testid="editorial-mission">
            <h2 className="text-xl md:text-2xl font-bold text-[#3a2a26] mb-3">Our Mission</h2>
            <p className="text-sm md:text-base text-[#5a4a40] leading-relaxed">
              Provide accurate, balanced, evidence-aware educational information about peptides studied in med spa and longevity research contexts. We aim to give readers and licensed clinicians a starting point for informed conversations — not a substitute for them.
            </p>
          </article>
        </div>
      </section>

      {/* Evidence Tiers */}
      <section className="py-10 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-8">
            <h2 className="text-2xl md:text-3xl font-bold text-[#3a2a26] mb-3">Three-Tier Evidence Framework</h2>
            <p className="text-sm md:text-base text-[#5a4a40] max-w-3xl">
              We classify every compound we discuss into one of three tiers. Tier dictates the language we use, the claims we permit, and how prominently we display regulatory context.
            </p>
          </div>
          <div className="space-y-4" data-testid="editorial-tiers">
            {EVIDENCE_TIERS.map((tier, i) => (
              <article
                key={tier.name}
                id={`tier-${i + 1}`}
                className={`border-2 ${tier.color} rounded-2xl p-6 scroll-mt-24`}
                data-testid={`editorial-tier-${i + 1}`}
              >
                <div className="flex items-start gap-3 mb-2">
                  <FlaskConical className={`w-5 h-5 ${tier.iconColor} flex-shrink-0 mt-1`} />
                  <h3 className="text-base md:text-lg font-bold text-[#3a2a26]">{tier.name}</h3>
                </div>
                <p className="text-sm md:text-base text-[#5a4a40] leading-relaxed mb-2">{tier.summary}</p>
                <p className="text-xs md:text-sm text-[#7a6b62] italic">{tier.examples}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Content Standards */}
      <section className="py-10 bg-[#fffaf5]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl md:text-3xl font-bold text-[#3a2a26] mb-6">Content Standards</h2>
          <ul className="space-y-3" data-testid="editorial-content-standards">
            {CONTENT_STANDARDS.map((cs) => (
              <li key={cs.strong} className="bg-white border border-[#f0d8d4] rounded-xl p-5 flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm md:text-base text-[#5a4a40] leading-relaxed">
                  <strong className="text-[#3a2a26]">{cs.strong}</strong> {cs.text}
                </p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* What we never publish */}
      <section className="py-10 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-rose-50 border-2 border-rose-300 rounded-2xl p-7" data-testid="editorial-never">
            <div className="flex items-center gap-3 mb-4">
              <XCircle className="w-6 h-6 text-rose-600" />
              <h2 className="text-xl md:text-2xl font-bold text-[#3a2a26]">What We Never Publish</h2>
            </div>
            <ul className="space-y-2">
              {NEVER_PUBLISH.map((item) => (
                <li key={item} className="flex items-start gap-2 text-sm md:text-base text-[#5a4a40]">
                  <span className="text-rose-600 mt-1">✗</span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
            <p className="text-xs text-[#7a6b62] italic mt-4">
              These exclusions are non-negotiable. Even if a piece of content would drive traffic, if it crosses a line above, we do not publish it.
            </p>
          </div>
        </div>
      </section>

      {/* Review Process */}
      <section className="py-10 bg-[#fffaf5]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl md:text-3xl font-bold text-[#3a2a26] mb-3 flex items-center gap-2">
            <Search className="w-6 h-6 text-[#b89a6d]" /> Our Review Process
          </h2>
          <p className="text-sm md:text-base text-[#5a4a40] mb-5">When creating or updating content, our editorial team:</p>
          <ol className="space-y-3 list-decimal list-inside" data-testid="editorial-process">
            {REVIEW_PROCESS.map((step, i) => (
              <li key={step} className="bg-white border border-[#f0d8d4] rounded-lg p-4 text-sm md:text-base text-[#5a4a40] leading-relaxed">
                <span className="text-[#b89a6d] font-semibold mr-1">{i + 1}.</span> {step}
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Editorial Independence */}
      <section className="py-10 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <article className="bg-[#fffaf5] border border-[#f0d8d4] rounded-2xl p-7" data-testid="editorial-independence">
            <h2 className="text-xl md:text-2xl font-bold text-[#3a2a26] mb-3">Editorial Independence &amp; Affiliate Disclosure</h2>
            <div className="text-sm md:text-base text-[#5a4a40] leading-relaxed space-y-3">
              <p>
                This site contains affiliate links to verified-provider partners (Paramount Peptides, Alpha BioMed Labs, AlphaSync Rx, MyDose.ai). Affiliate relationships are clearly labeled with the FTC-compliant <code className="bg-white border border-[#f0d8d4] px-1 py-0.5 rounded text-xs">rel=&quot;sponsored&quot;</code> attribute and marked as "Educational Referral Partners" wherever they appear.
              </p>
              <p>
                <strong className="text-[#3a2a26]">Affiliate partnerships do not influence content.</strong> We do not write favorable content in exchange for higher commissions. We do not omit safety, regulatory, or evidence-quality information because it might reduce sales. Editorial decisions are made independent of commercial pipelines.
              </p>
              <p>
                For our full Affiliate Disclosure see <Link to="/affiliate-program" className="text-[#b89a6d] hover:text-[#a08560] underline">/affiliate-program</Link>. For full compliance framework see <Link to="/compliance" className="text-[#b89a6d] hover:text-[#a08560] underline">/compliance</Link>.
              </p>
            </div>
          </article>
        </div>
      </section>

      {/* Updates & Corrections */}
      <section className="py-10 bg-[#fffaf5]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <article className="bg-white border border-[#f0d8d4] rounded-2xl p-7">
            <h2 className="text-xl md:text-2xl font-bold text-[#3a2a26] mb-3 flex items-center gap-2">
              <RefreshCw className="w-5 h-5 text-[#b89a6d]" /> Updates &amp; Corrections
            </h2>
            <div className="text-sm md:text-base text-[#5a4a40] space-y-3">
              <p>
                We review and update content periodically — particularly when regulatory status changes (e.g., new FDA action on a compound class) or significant new research emerges.
              </p>
              <p>
                If you identify an error or want to flag a content concern, contact{" "}
                <a href="mailto:everything@8eight8.net" className="text-[#b89a6d] hover:text-[#a08560] underline">everything@8eight8.net</a>.
                We correct material errors promptly and note significant corrections in-line.
              </p>
            </div>
          </article>
        </div>
      </section>

      {/* Compliance banner */}
      <section className="py-10 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-[#fdf2f4] border border-[#e8c5b8] rounded-xl p-6">
            <div className="flex items-start gap-3">
              <ShieldAlert className="w-5 h-5 text-[#b89a6d] flex-shrink-0 mt-0.5" />
              <p className="text-sm text-[#5a4a40] leading-relaxed">
                <strong className="text-[#b89a6d]">Education only.</strong> Information on this site is for educational and informational purposes only. Research peptides discussed may be investigational and may not be approved by FDA for therapeutic use. Nothing here is intended to diagnose, treat, cure, or prevent disease, or to provide medical advice. Consult a licensed healthcare professional before any wellness or aesthetic intervention.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Cite this page */}
      <section className="py-10 bg-[#fffaf5]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <CiteThisPage
            title="Editorial Standards — PeptidesForMedSpas"
            url={pageUrl}
            publishedDate="2026-05-21"
          />
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default EditorialStandardsPage;
